import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import ActivityTimeline from '../components/ActivityTimeline';
import ErrorState from '../components/ErrorState';
import { HiClock, HiArrowPath, HiListBullet, HiTableCells, HiSquares2X2 } from 'react-icons/hi2';

const toDate = (d, field) => {
  const v = d?.toMillis ? d : (d?.[field] ?? d);
  if (!v) return null;
  if (v?.toMillis) return new Date(v.toMillis());
  if (v instanceof Date) return v;
  return new Date(v);
};

const loadActivityData = async () => {
  const [actSnap, secSnap, stageSnap, docViewSnap, otpSnap] = await Promise.all([
    getDocs(
      query(collection(firestore, 'userActivity'), orderBy('createdAt', 'desc'), limit(12))
    ).catch(() => ({ docs: [] })),
    (async () => {
      try {
        return await getDocs(
          query(collection(firestore, 'security_logs'), orderBy('timestamp', 'desc'), limit(10))
        );
      } catch {
        try {
          return await getDocs(
            query(collection(firestore, 'security_logs'), orderBy('createdAt', 'desc'), limit(10))
          );
        } catch {
          return { docs: [] };
        }
      }
    })(),
    getDocs(
      query(collection(firestore, 'stageAuditLogs'), orderBy('affectedAt', 'desc'), limit(8))
    ).catch(() => ({ docs: [] })),
    getDocs(
      query(collection(firestore, 'documentBankViews'), orderBy('createdAt', 'desc'), limit(8))
    ).catch(() => ({ docs: [] })),
    getDocs(query(collection(firestore, 'otps'), orderBy('createdAt', 'desc'), limit(8))).catch(
      () => ({ docs: [] })
    ),
  ]);

  const activities = (actSnap?.docs || []).map(d => {
    const data = d.data();
    return {
      id: d.id,
      source: 'userActivity',
      ...data,
      createdAt: toDate(data.createdAt, 'createdAt') ?? data.createdAt,
    };
  });
  const security = (secSnap?.docs || []).map(d => {
    const data = d.data();
    const ts = data.timestamp ?? data.createdAt;
    return {
      id: d.id,
      source: 'securityLog',
      ...data,
      createdAt: toDate(ts, 'timestamp') ?? ts,
    };
  });
  const stageAudits = (stageSnap?.docs || []).map(d => {
    const data = d.data();
    return {
      id: d.id,
      source: 'stageAudit',
      ...data,
      createdAt: toDate(data.affectedAt, 'affectedAt') ?? data.affectedAt,
    };
  });
  const docViews = (docViewSnap?.docs || []).map(d => {
    const data = d.data();
    return {
      id: d.id,
      source: 'documentView',
      action: data.viewerName ? `Document viewed by ${data.viewerName}` : 'Document viewed',
      ...data,
      createdAt: toDate(data.createdAt, 'createdAt') ?? data.createdAt,
    };
  });
  const otps = (otpSnap?.docs || []).map(d => {
    const data = d.data();
    return {
      id: d.id,
      source: 'login',
      action: data.consumed ? 'OTP verified' : 'OTP requested',
      email: data.email,
      ...data,
      createdAt: toDate(data.createdAt, 'createdAt') ?? data.createdAt,
    };
  });

  return [...activities, ...security, ...stageAudits, ...docViews, ...otps];
};

const VIEW_MODES = [
  { id: 'timeline', label: 'Timeline', icon: HiListBullet },
  { id: 'table', label: 'Table', icon: HiTableCells },
  { id: 'cards', label: 'Cards', icon: HiSquares2X2 },
];

const DevActivityPage = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('timeline');

  const fetchActivity = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const data = await loadActivityData();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load activity');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchActivity, 30000);
    return () => clearInterval(interval);
  }, []);

  if (error) {
    return <ErrorState message={error} onRetry={fetchActivity} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/25">
            <HiClock className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Activity Timeline</h1>
            <p className="text-sm text-slate-400">
              Live logins, security events, document views, and platform activity
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-slate-600 bg-slate-800 p-1">
            {VIEW_MODES.map(mode => (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === mode.id
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                }`}
                title={mode.label}
              >
                <mode.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{mode.label}</span>
              </button>
            ))}
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={fetchActivity}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-blue-500/50 hover:bg-slate-700 disabled:opacity-60"
          >
            <HiArrowPath className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </motion.button>
        </div>
      </motion.header>

      <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
        <ActivityTimeline items={items} error={error} live viewMode={viewMode} />
      </div>
    </div>
  );
};

export default DevActivityPage;
